import React, { useState, useEffect } from 'react';
import { styles } from './adminStyles';

// Automated version of AdminBidOverrideModal's 'promote' mode: instead of an
// admin picking one standby bid, this previews (GET .../backfill-preview) and
// then commits (POST .../backfill-winners) a full re-rank of every rejected/
// explore bid against whatever budget/cap headroom is currently free — e.g.
// freed by a withdrawn winner. See backend/routes/admin.js's
// computeBackfillCandidates for the shared ranking logic behind both calls.
const AdminBackfillModal = ({ auctionId, onClose, onFetchPreview, onConfirmBackfill }) => {
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        onFetchPreview(auctionId)
            .then(data => { if (!cancelled) setPreview(data); })
            .catch(err => { if (!cancelled) setError(err.response?.data?.msg || 'Failed to load backfill preview.'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auctionId]);

    const handleConfirm = async () => {
        setError('');
        setIsSubmitting(true);
        try {
            const data = await onConfirmBackfill(auctionId);
            setResult(data);
        } catch (err) {
            setError(err.response?.data?.msg || 'Failed to run automated backfill.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const promotable = preview?.promotable || [];
    const skipped = preview?.skipped || [];

    return (
        <div style={styles.overlay} onClick={onClose}>
            <div style={{ ...styles.modal, width: '640px', height: 'auto', maxHeight: '85vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
                <div style={{ padding: '20px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#F8FAFC' }}>
                    <h2 style={{ margin: 0, ...styles.h2, color: '#0D9488' }}>Backfill Winners</h2>
                    <button onClick={onClose} style={{ ...styles.btnReversed, padding: '8px 16px' }}>Close ✕</button>
                </div>

                <div style={{ padding: '20px' }}>
                    {result ? (
                        <p style={{ fontSize: '0.9rem', color: '#0F172A', fontFamily: '"Inter", sans-serif' }}>{result.msg}</p>
                    ) : (
                        <>
                            <p style={{ fontSize: '0.85rem', color: '#475569', fontFamily: '"Inter", sans-serif', marginTop: 0 }}>
                                Re-ranks every currently rejected/explore bid against whatever budget/cap headroom is free right now, and promotes whichever fit — at their own submitted price. Only newly-promoted farmers are emailed; everything else is left untouched.
                            </p>

                            {loading ? (
                                <p style={styles.textMuted}>Loading candidates...</p>
                            ) : (
                                <>
                                    <label style={styles.label}>Would Be Promoted ({promotable.length})</label>
                                    {promotable.length === 0 ? (
                                        <p style={{ ...styles.textMuted, marginTop: 0 }}>No standby bid currently fits the available headroom.</p>
                                    ) : (
                                        <div style={{ marginBottom: '15px', border: '1px solid #eee', borderRadius: '4px', overflow: 'hidden' }}>
                                            {promotable.map(p => (
                                                <div key={p.bidId} style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9', fontSize: '0.85rem', fontFamily: '"Inter", sans-serif', display: 'flex', justifyContent: 'space-between' }}>
                                                    <span><strong>{p.farmer}</strong> — {p.action} ({p.fieldName})</span>
                                                    <span>£{Number(p.amount).toLocaleString('en-GB')} · BCR {Number(p.benefitCostRatio).toFixed(2)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {skipped.length > 0 && (
                                        <>
                                            <label style={styles.label}>Considered, Not Promoted ({skipped.length})</label>
                                            <div style={{ marginBottom: '15px', border: '1px solid #eee', borderRadius: '4px', overflow: 'hidden' }}>
                                                {skipped.map(s => (
                                                    <div key={s.bidId} style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9', fontSize: '0.85rem', fontFamily: '"Inter", sans-serif', color: '#475569', display: 'flex', justifyContent: 'space-between' }}>
                                                        <span>{s.farmer} — {s.action}</span>
                                                        <span>{s.reason}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </>
                            )}
                        </>
                    )}

                    {error && <p style={{ color: '#DC2626', fontSize: '0.85rem', fontFamily: '"Inter", sans-serif' }}>{error}</p>}

                    {!result && (
                        <button
                            type="button"
                            disabled={loading || isSubmitting || promotable.length === 0}
                            onClick={handleConfirm}
                            style={{ ...styles.btnPrimary, width: '100%', opacity: (loading || isSubmitting || promotable.length === 0) ? 0.6 : 1, cursor: (loading || isSubmitting || promotable.length === 0) ? 'not-allowed' : 'pointer' }}
                        >
                            {isSubmitting ? 'Promoting...' : `Confirm — Promote ${promotable.length} Bid(s)`}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminBackfillModal;
